import { FC } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { imageAmplienceToUrl } from 'helpers/utils/amplience/imageAmplienceToUrl';
import { amplienceImageLoader } from 'helpers/utils/amplience/imageLoader';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop } from 'helpers/utils/screensizes';
import { layout } from 'static/constants';
import { AmpPromotionEvent } from '../../../../types/gtm-events/Ecommerce';
import GTMWrapper from '../gtm-wrapper';
import AmplienceText, { AmplienceTextProps } from '../text';
import { AmplienceImage } from '../types';

export enum ImageAlign {
  LEFT = 'Left',
  MIDDLE = 'Middle',
  RIGHT = 'Right',
}

const alignmentClasses: Record<ImageAlign, string> = {
  [ImageAlign.LEFT]: 'mr-auto',
  [ImageAlign.MIDDLE]: 'mx-auto',
  [ImageAlign.RIGHT]: 'ml-auto',
};

export interface AmplienceImageComponentProps {
  image: AmplienceImage;
  mobileImage?: AmplienceImage;
  width: number;
  align: ImageAlign;
  priority?: boolean;
  seo: {
    ariaLabel?: string;
    altText: string;
  };
  caption?: {
    content: AmplienceTextProps;
  };
  url?: string;
  gtmEvents: AmpPromotionEvent[];
}

const AmplienceImageComponent: FC<AmplienceImageComponentProps> = ({
  image,
  width,
  align,
  seo,
  caption,
  priority = false,
  url,
  gtmEvents,
  mobileImage,
}) => {
  const maxImageWidth = Math.round((width / 100) * layout.pageMaxWidth);
  const imageSizes = `(max-width: 768px) 100vw, ${maxImageWidth}px`;
  const [isDesktop] = useMediaQuery(desktop);
  const _image = isDesktop ? image : mobileImage || image;

  return (
    <GTMWrapper gtmEvents={gtmEvents}>
      {url ? (
        <Link href={url} aria-label={seo.ariaLabel} data-testid="next-link">
          <figure style={{ width: `${width}%` }} className={alignmentClasses[align] || 'mx-auto'}>
            <Image
              loader={amplienceImageLoader}
              src={imageAmplienceToUrl(_image)}
              width={1000}
              height={1000}
              style={{ width: '100%', height: 'auto' }}
              sizes={imageSizes}
              alt={seo.altText}
              className="rounded-md"
              priority={priority}
              loading={priority ? 'eager' : 'lazy'}
              fetchPriority={priority ? 'high' : 'auto'}
            />
            {caption && caption.content && (
              <figcaption className="mt-2 text-sm text-black" id={`caption-${image.id}`}>
                <AmplienceText {...caption?.content} />
              </figcaption>
            )}
          </figure>
        </Link>
      ) : (
        <figure style={{ width: `${width}%` }} className={alignmentClasses[align] || 'mx-auto'}>
          <Image
            loader={amplienceImageLoader}
            src={imageAmplienceToUrl(_image)}
            width={1000}
            height={1000}
            style={{ width: '100%', height: 'auto' }}
            sizes={imageSizes}
            alt={seo.altText}
            className="rounded-md"
            aria-label={seo.ariaLabel}
            priority={priority}
            loading={priority ? 'eager' : 'lazy'}
            fetchPriority={priority ? 'high' : 'auto'}
          />
          {caption && caption.content && (
            <figcaption className="mt-2 text-sm text-black" id={`caption-${image.id}`}>
              <AmplienceText {...caption?.content} />
            </figcaption>
          )}
        </figure>
      )}
    </GTMWrapper>
  );
};

export default AmplienceImageComponent;
